/**
 * The two cards over the usage card, split like the idea matrix tiles:
 *
 * - Budget: one segmented bar for the period's cap, spent, then in flight,
 *   then remaining, so the three parts read against the whole. A tick marks
 *   the alert threshold; in the current period a second tick marks where
 *   spend would be on an even pace, and a month projects its month-end spend.
 * - Activity: the runs with one 100% bar of their outcomes, and the tokens
 *   as small columns per day (per month in a year), up to today.
 *
 * Plain HTML and CSS: no chart library.
 */

import type { Usage } from '@/api/types';
import { currentMonth, formatCompact, formatInt, formatPct, formatUsd, share } from '@/ui/format';

import { budgetFigures, STATE_BADGE, type BudgetFigures } from './budgetFigures';
import { daysOf, isCurrentPeriod, isYear } from './usageUi';

export function UsageSummary({ data }: { data: Usage }) {
  return (
    <div className="metric-area">
      <div className="metric-row usage-summary">
        <BudgetCard data={data} />
        <span className="metric-divider" aria-hidden="true" />
        <ActivityCard data={data} />
      </div>
    </div>
  );
}

function BudgetCard({ data }: { data: Usage }) {
  const f = budgetFigures(data);
  const { budget, state, cap, spent } = f;
  return (
    <section className="metric-tile usage-card usage-card-budget" aria-label="Budget">
      <div className="usage-card-head">
        <div className="usage-card-figure">
          <span className="metric-value">{formatUsd(spent)}</span>
          <span className="usage-card-of">of {formatUsd(cap)} budget</span>
        </div>
        <span className={`badge ${STATE_BADGE[state]}`}>
          {formatPct(budget.used_pct)} · {f.status}
        </span>
      </div>

      <BudgetBar f={f} />
      <BudgetLegend f={f} />

      <p className="usage-card-note">
        {f.projected !== null && (
          <>
            Projected month end <b className={f.projected > cap ? 'usage-over' : undefined}>{formatUsd(f.projected)}</b> ·{' '}
          </>
        )}
        {f.capNote}
      </p>
    </section>
  );
}

function BudgetBar({ f }: { f: BudgetFigures }) {
  const { budget, cap, spent, inFlight, whole, paceUsd } = f;
  return (
    <div
      className={`stack-bar budget-bar is-${f.state}`}
      role="img"
      aria-label={`${formatUsd(spent)} spent, ${formatUsd(inFlight)} in flight, ${formatUsd(f.remaining)} remaining of ${formatUsd(cap)}`}
    >
      <span className="seg seg-spent" style={{ width: share(spent, whole) }} />
      <span className="seg seg-flight" style={{ width: share(inFlight, whole) }} />
      {cap > 0 && budget.alert_threshold_pct < 100 && (
        <span className="bar-mark mark-alert" style={{ left: `${(budget.alert_threshold_pct * cap) / whole}%` }} title={`Alert at ${budget.alert_threshold_pct}%`} />
      )}
      {paceUsd !== null && cap > 0 && (
        <span className="bar-mark mark-pace" style={{ left: `${(paceUsd / whole) * 100}%` }} title={`Even pace today: ${formatUsd(paceUsd)}`} />
      )}
    </div>
  );
}

function BudgetLegend({ f }: { f: BudgetFigures }) {
  const { spent, paceUsd } = f;
  return (
    <ul className="usage-legend">
      <li><span className="swatch seg-spent" />Spent <b>{formatUsd(spent)}</b></li>
      <li title="Reserved by calls still running"><span className="swatch seg-flight" />In flight <b>{formatUsd(f.inFlight)}</b></li>
      <li><span className="swatch seg-left" />Remaining <b>{formatUsd(f.remaining)}</b></li>
      {paceUsd !== null && (
        <li title="Where spend would be today if the budget were used evenly">
          <span className="swatch swatch-tick" />
          {spent <= paceUsd ? 'Under' : 'Over'} pace <b>{formatUsd(paceUsd)}</b>
        </li>
      )}
    </ul>
  );
}

const OUTCOMES = [
  { key: 'succeeded', label: 'Succeeded', className: 'seg-ok' },
  { key: 'failed', label: 'Failed', className: 'seg-failed' },
  { key: 'refused', label: 'Refused', className: 'seg-refused', title: 'Stopped by the budget cap' },
  { key: 'other', label: 'Running / abandoned', className: 'seg-other' },
] as const;

function ActivityCard({ data }: { data: Usage }) {
  const t = data.totals;
  const counts = {
    succeeded: t.succeeded,
    failed: t.failed,
    refused: t.refused,
    other: Math.max(t.runs - t.succeeded - t.failed - t.refused, 0),
  };
  const tokens = t.tokens_input + t.tokens_output;
  const successPct = t.runs > 0 ? (t.succeeded / t.runs) * 100 : null;
  const cachePct = t.tokens_input > 0 ? (t.tokens_cached / t.tokens_input) * 100 : 0;

  return (
    <section className="usage-card-pair" aria-label="Activity">
      <div className="metric-tile usage-card">
        <div className="usage-card-head">
          <div className="usage-card-figure">
            <span className="metric-value">{formatInt(t.runs)}</span>
            <span className="usage-card-of">runs</span>
          </div>
          {successPct !== null && <span className="usage-card-of">{formatPct(successPct)} succeeded</span>}
        </div>
        <div
          className="stack-bar outcome-bar"
          role="img"
          aria-label={OUTCOMES.map((o) => `${formatInt(counts[o.key])} ${o.label.toLowerCase()}`).join(', ')}
        >
          {OUTCOMES.map((o) => (
            <span key={o.key} className={`seg ${o.className}`} style={{ width: share(counts[o.key], t.runs) }} />
          ))}
        </div>
        <ul className="usage-legend">
          {OUTCOMES.filter((o) => o.key !== 'other' || counts.other > 0).map((o) => (
            <li key={o.key} title={'title' in o ? o.title : undefined}>
              <span className={`swatch ${o.className}`} />
              {o.label} <b>{formatInt(counts[o.key])}</b>
            </li>
          ))}
        </ul>
      </div>

      <div className="metric-tile usage-card">
        <div className="usage-card-head">
          <div className="usage-card-figure">
            <span className="metric-value">{formatCompact(tokens)}</span>
            <span className="usage-card-of">tokens</span>
          </div>
          <span className="usage-card-of">{formatPct(cachePct)} cached</span>
        </div>
        <TokenSparkline data={data} />
        <p className="usage-card-note">
          {formatInt(t.tokens_input)} in · {formatInt(t.tokens_output)} out · {formatInt(t.tokens_cached)} cached
        </p>
      </div>
    </section>
  );
}

/** Tokens per day (per month in a year), up to today, as small columns. */
function TokenSparkline({ data }: { data: Usage }) {
  const year = isYear(data.month);
  const rows = year ? data.by_month : data.by_day;
  let keys = year ? Array.from({ length: 12 }, (_, i) => `${data.month}-${String(i + 1).padStart(2, '0')}`) : daysOf(data.month);
  if (isCurrentPeriod(data.month)) {
    const today = year ? currentMonth() : `${currentMonth()}-${String(new Date().getDate()).padStart(2, '0')}`;
    keys = keys.filter((k) => k <= today);
  }
  const byKey = new Map(rows.map((r) => [r.key, r.tokens_input + r.tokens_output]));
  const values = keys.map((k) => byKey.get(k) ?? 0);
  const max = Math.max(...values, 0);
  const unit = year ? 'month' : 'day';

  if (max === 0) {
    return <div className="sparkline sparkline-empty">No tokens used yet</div>;
  }
  return (
    <div className="sparkline" role="img" aria-label={`Tokens per ${unit}, peak ${formatCompact(max)}`}>
      {keys.map((k, i) => (
        <span
          key={k}
          className="spark-col"
          style={{ height: `${(values[i]! / max) * 100}%` }}
          title={`${k}: ${formatInt(values[i]!)} tokens`}
        />
      ))}
    </div>
  );
}
